import React from 'react';

import { UnstyledButton } from '@mantine/core';
import { IconArrowsLeftRight } from '@tabler/icons-react';
import type { CellContext, ColumnDef } from '@tanstack/react-table';

import { ClonotypeSelectCell, ClonotypeSelectHeader } from './ClonotypeSelectColumn';
import { IsotypeBadgeCell } from './IsotypeBadgeCell';
import { MiniNetworkGraphSvg } from '../../../clonotype-network-map/MiniNetworkGraphSvg';
import type { ClonotypeRow } from '../../types/antibodyTable';
import { highlightText } from '../../utilities/highlightText';
import { FilterableHeader } from '../common/FilterableHeader';

const NETWORK_MAP_COL_WIDTH = 110;
const NETWORK_MAP_COL_HEIGHT = 90;

const s = {
  cellId: 'font-semibold text-sm',
  cellText: 'text-sm',
  cellTruncate: 'text-xs truncate block',
  cellMono: 'text-xs font-mono truncate block',
  networkMapCell: 'flex flex-col items-center gap-1',
  tanglegramButton: 'inline-flex items-center gap-1 px-2 py-0.5 rounded hover:bg-[#0064B0]/10',
} as const;

const stopCellClick = (e: React.MouseEvent) => e.stopPropagation();

function NetworkMapCell({ row, table }: CellContext<ClonotypeRow, unknown>) {
  const { meta } = table.options;
  const clonotype = meta?.networkMapByClonotypeId?.get(row.original.clonotypeId);
  if (!clonotype) {
    return null;
  }
  const onTanglegramClick = meta?.onTanglegramClick;
  return (
    <div onClick={stopCellClick} className={s.networkMapCell}>
      <MiniNetworkGraphSvg clonotype={clonotype} width={NETWORK_MAP_COL_WIDTH} height={NETWORK_MAP_COL_HEIGHT} />
      {onTanglegramClick && (
        <UnstyledButton onClick={() => onTanglegramClick(clonotype)} c="#0064B0" fz={12} fw={500} className={s.tanglegramButton}>
          <IconArrowsLeftRight size={14} />
          Tanglegram
        </UnstyledButton>
      )}
    </div>
  );
}

export interface OuterClonotypeTableColumnsParams {
  allSelected: boolean;
  someSelected: boolean;
  handleSelectAll: () => void;
  /** Row checkbox state: `checked` = all the clonotype's sequences selected for export, `indeterminate` = some. */
  clonotypeSelectionState: (row: ClonotypeRow) => { checked: boolean; indeterminate: boolean };
  /** Check/uncheck all the clonotype's sequences for export (does NOT change network-map selection). */
  handleToggleClonotypeChecked: (row: ClonotypeRow) => void;
  searchQuery: string;
  columnValues: {
    igIsotype: string[];
    vjGeneHeavy: string[];
    vjGeneLight: string[];
    representativeCDRH3: string[];
    representativeCDRL3: string[];
  };
}

/**
 * function for OuterParentClonotypeTable column definitions.
 * Returns a plain array.
 */
export function OuterClonotypeTableColumns({
  allSelected,
  someSelected,
  handleSelectAll,
  clonotypeSelectionState,
  handleToggleClonotypeChecked,
  searchQuery,
  columnValues,
}: OuterClonotypeTableColumnsParams): ColumnDef<ClonotypeRow>[] {
  return [
    {
      id: 'select',
      header: () => <ClonotypeSelectHeader allSelected={allSelected} someSelected={someSelected} onSelectAll={handleSelectAll} />,
      cell: ({ row }) => {
        const { checked, indeterminate } = clonotypeSelectionState(row.original);
        return <ClonotypeSelectCell row={row.original} checked={checked} indeterminate={indeterminate} onToggle={handleToggleClonotypeChecked} />;
      },
      size: 36,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
    },
    {
      id: 'networkMap',
      header: 'Network map',
      cell: NetworkMapCell,
      size: NETWORK_MAP_COL_WIDTH + 16,
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: false,
    },
    {
      id: 'clonotypeId',
      header: 'Clonotype ID',
      accessorKey: 'clonotypeId',
      cell: ({ row }) => <span className={s.cellId}>{highlightText(row.original.clonotypeId.toString(), searchQuery)}</span>,
      size: 110,
      enableColumnFilter: false,
      enableHiding: false,
    },
    {
      id: 'clonotypeSize',
      header: ({ column }) => <FilterableHeader title="Clonotype size" column={column} showFilter={false} />,
      accessorKey: 'clonotypeSize',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.clonotypeSize.toString(), searchQuery)}</span>,
      size: 145,
      enableColumnFilter: false,
    },
    {
      id: 'numberOfNodes',
      header: ({ column }) => <FilterableHeader title="Number of nodes" column={column} showFilter={false} />,
      accessorKey: 'numberOfNodes',
      cell: ({ row }) => <span className={s.cellText}>{highlightText(row.original.numberOfNodes.toString(), searchQuery)}</span>,
      size: 160,
      enableColumnFilter: false,
    },
    {
      id: 'igIsotype',
      header: ({ column }) => <FilterableHeader title="Ig isotype" column={column} allValues={columnValues.igIsotype} />,
      accessorKey: 'igIsotype',
      cell: ({ row }) => <IsotypeBadgeCell row={row.original} />,
      filterFn: 'includesString',
      enableColumnFilter: true,
      size: 120,
    },
    {
      id: 'vjGeneHeavy',
      header: ({ column }) => <FilterableHeader title="V-J gene (heavy)" column={column} allValues={columnValues.vjGeneHeavy} />,
      accessorKey: 'vjGeneHeavy',
      cell: ({ row }) => (
        <span className={s.cellTruncate} title={row.original.vjGeneHeavy}>
          {highlightText(row.original.vjGeneHeavy, searchQuery)}
        </span>
      ),
      filterFn: 'includesString',
      enableColumnFilter: true,
      size: 160,
    },
    {
      id: 'vjGeneLight',
      header: ({ column }) => <FilterableHeader title="V-J gene (light)" column={column} allValues={columnValues.vjGeneLight} />,
      accessorKey: 'vjGeneLight',
      cell: ({ row }) => (
        <span className={s.cellTruncate} title={row.original.vjGeneLight}>
          {highlightText(row.original.vjGeneLight, searchQuery)}
        </span>
      ),
      filterFn: 'includesString',
      enableColumnFilter: true,
      size: 160,
    },
    {
      id: 'representativeCDRH3',
      header: ({ column }) => <FilterableHeader title="Representative CDRH3" column={column} allValues={columnValues.representativeCDRH3} />,
      accessorKey: 'representativeCDRH3',
      cell: ({ row }) => (
        <span className={s.cellMono} title={row.original.representativeCDRH3}>
          {highlightText(row.original.representativeCDRH3, searchQuery)}
        </span>
      ),
      filterFn: 'includesString',
      enableColumnFilter: true,
      size: 200,
    },
    {
      id: 'representativeCDRL3',
      header: ({ column }) => <FilterableHeader title="Representative CDRL3" column={column} allValues={columnValues.representativeCDRL3} />,
      accessorKey: 'representativeCDRL3',
      cell: ({ row }) => (
        <span className={s.cellMono} title={row.original.representativeCDRL3}>
          {highlightText(row.original.representativeCDRL3, searchQuery)}
        </span>
      ),
      filterFn: 'includesString',
      enableColumnFilter: true,
      size: 200,
    },
  ];
}
